import React from 'react';
import {
  Box,
  Button,
  Flex,
  Spacer,
  BoxProps,
  Tag,
  Tooltip,
} from '@chakra-ui/react';
import {PageHeader} from '../PageHeader';
import {Link} from 'react-router-dom';
import {ObjectId} from 'bson';
import {pluralize} from '../../lib/helpers';
import {
  useCustomer,
  useActiveMarketId,
  useIsFreeOrListBuilderCustomer,
  useMarket,
  useMarkets,
} from '../../hooks/api/metadata';
import {Market} from '../../shared/market';
import {useCreateMarket} from '../../hooks/api/markets';
import {getLimit} from '../../shared/customer';
import {MarketProvider} from './MarketProvider';
import {EditableMarketLabel} from './MarketBuilder/EditableMarketLabel';
import {ActivateMarketButton} from './ActivateMarketButton';

const buttonLinkWrapperStyle: BoxProps = {
  outline: 'transparent solid 2px',
  outlineOffset: '2px',
};

const buttonLinkProps: BoxProps = {
  borderWidth: 1,
  borderColor: 'kblue.400',
  borderRadius: '8px',
  color: 'kblue.400',
  fontWeight: 500,
  fontSize: 14,
  px: 6,
  py: 3,
  transitionDuration: '200ms',
  transitionProperty:
    'background-color,border-color,color,fill,stroke,opacity,box-shadow,transform;',
  _hover: {
    textDecoration: 'none',
    bg: 'kblue.50',
  },
  _groupFocusVisible: {
    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6);',
  },
};

const SummaryTags = ({marketId}: {marketId: ObjectId}) => {
  const activeMarketId = useActiveMarketId();
  const market = useMarket(marketId);
  if (!market) {
    return <></>;
  }

  const {scoringModel} = market;
  const numLookalikes = scoringModel.lookalikes?.length ?? 0;
  const numSignals = scoringModel.signalsWithWeights?.length ?? 0;

  const tags = [
    pluralize(numLookalikes, 'lookalike', 'lookalikes'),
    pluralize(numSignals, 'signal', 'signals'),
  ];
  return (
    <>
      {tags.map((t) => (
        <Tag
          color="kgray.300"
          variant="outline"
          key={t}
          mr={2}
          size={'sm'}
          fontWeight={300}
          fontSize={10}
          textTransform="uppercase"
        >
          {t}
        </Tag>
      ))}
      {activeMarketId.equals(marketId) && (
        <Tag
          key="default"
          mr={2}
          size={'sm'}
          fontWeight={300}
          fontSize={10}
          textTransform="uppercase"
        >
          Active
        </Tag>
      )}
    </>
  );
};

const MarketRow = ({market}: {market: Market}) => {
  const activeMarketId = useActiveMarketId();

  return (
    <Box bg="white" px={8} pt={4} key={market.id.toString()}>
      <Box borderRadius={8} borderColor="kgray.200" borderWidth={1} p={8}>
        <Flex alignItems="center">
          <MarketProvider market={market}>
            <EditableMarketLabel fontSize={20} fontWeight={500} mr={4} />
          </MarketProvider>
          <SummaryTags marketId={market.id} />
          <Spacer />
          {!activeMarketId.equals(market.id) && (
            <ActivateMarketButton activeMarketId={market.id} mr={4} />
          )}
          <Box
            as={Link}
            role="group"
            {...buttonLinkWrapperStyle}
            to={`/markets/${market.id}`}
            mr={2}
          >
            <Box {...buttonLinkProps}>Adjust Market &amp; Scoring</Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export const MarketsList = () => {
  const markets = useMarkets();
  const customer = useCustomer();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const createMarket = useCreateMarket();

  const addMarketTooltip = isFreeOrListBuilderCustomer
    ? 'Contact us to schedule a demo at hello@keyplay.io'
    : markets.length >= getLimit(customer, 'markets')
      ? 'Contact us to add additional markets to your plan at hello@keyplay.io'
      : '';
  const addMarketDisabled = !!addMarketTooltip;

  return (
    <Box>
      <PageHeader px={8} title="Markets">
        <Tooltip
          hasArrow
          label={addMarketTooltip}
          bg="black"
          placement="bottom-end"
          isDisabled={!addMarketDisabled}
        >
          <Button
            isDisabled={addMarketDisabled}
            colorScheme="kbuttonblue"
            onClick={() => createMarket.mutate({})}
            isLoading={createMarket.isPending}
          >
            New Market
          </Button>
        </Tooltip>
      </PageHeader>
      <Box color="kgray.300" mx={8} whiteSpace="nowrap" mt={8}>
        {pluralize(markets.length, 'market', 'markets')}
      </Box>
      {markets.map((market) => (
        <MarketRow market={market} key={market.id.toString()} />
      ))}
    </Box>
  );
};
